import { Grid, List, ListItem, ListItemButton } from '@mui/material';
import Badge from '@mui/material/Badge';
import { useSocketCtx } from 'packages/workspace/modules/helpers/contexts/socket';
import useOnlineStatus from 'packages/workspace/modules/prelude/useOnlineStatus';
import useSession from 'packages/workspace/services/store/session-store';
import { useEffect, useRef, useState } from 'react';
import { InternalChatContainer, StaffChatNotification } from '../common/internal-chat/InternalChatContainer';
import { SubmitBug } from '../common/SubmitBug';
import InternalOnCall from './InternalOnCall';
import ShoutOut from './ShoutOut';
import { ChatConnectionError } from '../common/internal-chat/ChatConnectionError';
import StafferVisitorOneonOne from 'packages/workspace/public/sounds/staffer_visitor_one_on_one.ogg';
import useSound from 'use-sound';
import useSoundStore, { SoundOptions } from 'packages/workspace/services/store/useSoundStore';
import InternalAdminOnCall from './InternalAdminOnCall';
import { MdBugReport, MdMessage, MdPeopleOutline } from 'react-icons/md';
import useOnlineUsers from 'packages/workspace/modules/datastores/internal-chat/useOnlineUsers';
import useInternalConversationStore from 'packages/workspace/services/store/omnichannel/internal-conversation-store';

const DrawerConstants = {
  INTERNAL_CHAT_DRAWER: 'internalChat',
  BUG_REPORT: 'bugReport',
  INTERNAL_ONCALL: 'internalOnCall',
  INTERNAL_ADMIN_ONCALL: 'internalAdminOnCall',
  EXTERNAL_CALLING: 'externalCalling',
};

const RhsFooter: React.FC = () => {
  const workspaceNameRef = useRef<HTMLElement | null>(null);
  const session = useSession();
  const { client, error } = useSocketCtx();
  const online = useOnlineStatus();
  const [drawerState, setDrawerState] = useState({ drawer: '', right: false, modal: false });
  const [stafferVisitorOneonOne] = useSound(StafferVisitorOneonOne);
  const sound = useSoundStore();
  const usersQuery = useOnlineUsers();
  const convState = useInternalConversationStore();

  useEffect(() => {
    if (sound?.mute && sound.type.startsWith('Internal_Chat')) {
      stafferVisitorOneonOne();
    }
    return () => SoundOptions.setType('');
  }, [stafferVisitorOneonOne, sound?.type, sound?.mute]);

  const toggleDrawer = (drawer: string, anchor: string, open: boolean) => () => {
    setDrawerState((oldDrawerState) => ({ ...oldDrawerState, drawer, [anchor]: open }));
  };

  useEffect(() => {
    if (workspaceNameRef.current) {
      workspaceNameRef.current.click();
    }
  }, [workspaceNameRef]);

  return (
    <>
      <SubmitBug open={drawerState.drawer === DrawerConstants.BUG_REPORT && drawerState.right} onClose={toggleDrawer(DrawerConstants.BUG_REPORT, 'right', false)} />
      <InternalChatContainer
        user_id={session.iamPrincipal?.id}
        open={drawerState.drawer === DrawerConstants.INTERNAL_CHAT_DRAWER && drawerState.right}
        onClose={toggleDrawer(DrawerConstants.INTERNAL_CHAT_DRAWER, 'right', false)}
      />
      {/* <footer className="h-8 ml-16 px-3 py-1 bg-tc-1 border-t border-tc-6 fixed bottom-0 left-0 right-0 flex justify-between"> */}
      <footer className="h-8 px-3 py-1 bg-tc-1 border-t border-tc-6 flex justify-between items-center">
        <div>
          <ul className="flex">
            <li className="flex items-center py-1 px-2 text-tc-4 font-interbold hover:text-tc-2">
              <Badge className="rhs-badge available mr-2" variant="dot" />
              <span className="text-xs" ref={workspaceNameRef}>
                {session.discoveryParams?.workspace_name}
              </span>
            </li>
          </ul>
        </div>
        <div>
          <nav>
            <List className="flex" sx={{ padding: 0 }}>
              {online && client?.connected ? (
                <ListItem disablePadding className="flex items-center py-1 px-4 text-tc-4 font-interbold hover:text-tc-2 cursor-pointer" sx={{ width: 'unset' }}>
                  <StaffChatNotification />
                  <ListItemButton onClick={toggleDrawer(DrawerConstants.INTERNAL_CHAT_DRAWER, 'right', true)} sx={{ padding: 0, '&:focus': { background: 'unset' } }}>
                    <MdPeopleOutline className="mr-1 text-base" />
                    <span className="text-xs">Staff ({usersQuery?.data?.filter((eachUser) => eachUser?.online).length || 0})</span>
                    &nbsp;&nbsp;
                    {convState.total_messages > 0 && (
                      <Badge badgeContent={convState?.total_messages} color="primary" max={20}>
                        <MdMessage className="mr-1 text-base" />
                      </Badge>
                    )}
                  </ListItemButton>
                </ListItem>
              ) : (
                <ChatConnectionError error={error} />
              )}
              <ListItem disablePadding className="flex items-center px-4 text-tc-4 font-interbold hover:text-tc-2 cursor-pointer" sx={{ width: 'unset' }}>
                <ListItemButton onClick={toggleDrawer(DrawerConstants.BUG_REPORT, 'right', true)} sx={{ padding: 0, '&:focus': { background: 'unset' } }}>
                  <MdBugReport className="mr-1 text-base" />
                  <span className="text-xs">Report Bug</span>
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding className="flex items-center px-4 text-tc-4 font-interbold hover:text-tc-2 cursor-pointer" sx={{ width: 'unset' }}>
                <ListItemButton onClick={toggleDrawer(DrawerConstants.INTERNAL_ADMIN_ONCALL, 'modal', true)} sx={{ padding: 0, '&:focus': { background: 'unset' } }}>
                  <InternalAdminOnCall
                    open={drawerState.drawer === DrawerConstants.INTERNAL_ADMIN_ONCALL && drawerState.modal}
                    onClose={toggleDrawer(DrawerConstants.INTERNAL_ADMIN_ONCALL, 'modal', false)}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding className="flex items-center px-4 text-tc-4 font-interbold hover:text-tc-2 cursor-pointer" sx={{ width: 'unset' }}>
                <ListItemButton onClick={toggleDrawer(DrawerConstants.INTERNAL_ONCALL, 'modal', true)} sx={{ padding: 0, '&:focus': { background: 'unset' } }}>
                  <InternalOnCall open={drawerState.drawer === DrawerConstants.INTERNAL_ONCALL && drawerState.modal} onClose={toggleDrawer(DrawerConstants.INTERNAL_ONCALL, 'modal', false)} />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding className="flex items-center px-4 text-tc-4 font-interbold hover:text-tc-2 cursor-pointer" sx={{ width: 'unset' }}>
                <ListItemButton onClick={toggleDrawer(DrawerConstants.EXTERNAL_CALLING, 'modal', true)} sx={{ padding: 0, '&:focus': { background: 'unset' } }}>
                  <ShoutOut open={drawerState.drawer === DrawerConstants.EXTERNAL_CALLING && drawerState.modal} onClose={toggleDrawer(DrawerConstants.EXTERNAL_CALLING, 'modal', false)} />
                </ListItemButton>
              </ListItem>
              {/* Commenting out till its functional.
              <ListItem disablePadding className="flex items-center px-4 text-tc-4 font-interbold hover:text-tc-2 cursor-pointer" sx={{width: 'unset'}}>
                <ListItemButton sx={{padding: 0.25, '&:focus': {background: 'unset'}}}>
                  <ListItemIcon sx={{minWidth: 'unset'}} className='text-tc-4 font-interbold'>
                    <Badge className="rhs-badge dnd mr-1" variant="dot">
                      <MdNotificationsNone className="text-base" />
                    </Badge>
                  </ListItemIcon>
                  <ListItemText primary={'0'} primaryTypographyProps={{className: 'text-xs text-tc-4 font-interbold'}}/>
                </ListItemButton>
              </ListItem> */}
            </List>
          </nav>
        </div>
      </footer>
    </>
  );
};

export default RhsFooter;
